import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { canViewIntakes } from '@/lib/project-permissions';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  FolderOpen,
  Settings,
  Users,
  UserCog,
  Map,
  LogOut,
  ShieldCheck,
  Inbox,
  PanelLeftClose,
  PanelLeftOpen,
  ChevronUp,
  LayoutDashboard,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';

const STORAGE_KEY = 'dp:sidebar-collapsed';
const COLLAPSE_BREAKPOINT = 1280;

function getInitialCollapsed(): boolean {
  if (typeof window === 'undefined') return false;
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored !== null) return stored === 'true';
  return window.matchMedia(`(max-width: ${COLLAPSE_BREAKPOINT - 1}px)`).matches;
}

interface NavItem {
  label: string;
  to: string;
  icon: ReactNode;
  adminOnly?: boolean;
  /** Hidden for roles that may not use the page (see canViewIntakes). */
  intakes?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  // T-44a: leadership/admin home. `adminOnly` here means leadership OR admin (see isAdmin).
  { label: 'Dashboard',      to: '/dashboard', icon: <LayoutDashboard size={20} />, adminOnly: true },
  { label: 'Projects',       to: '/projects',  icon: <FolderOpen size={20} /> },
  { label: 'Intakes',        to: '/intakes',   icon: <Inbox size={20} />, intakes: true },
  { label: 'Resources',      to: '/resources', icon: <Users size={20} /> },
  { label: 'Users',          to: '/users',     icon: <UserCog size={20} />, adminOnly: true },
  { label: 'CASDM Explorer', to: '/casdm',     icon: <Map size={20} /> },
  { label: 'Settings',       to: '/settings',  icon: <Settings size={20} /> },
];

function initials(name: string | undefined): string {
  if (!name) return 'U';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'U';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

export default function AppShell({ children }: { children: ReactNode }): JSX.Element {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [collapsed, setCollapsed] = useState(getInitialCollapsed);
  const userToggledRef = useRef(
    typeof window !== 'undefined' && window.localStorage.getItem(STORAGE_KEY) !== null,
  );
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const accountMenuRef = useRef<HTMLDivElement>(null);

  // Close account menu on outside click or Escape
  useEffect(() => {
    if (!accountMenuOpen) return;
    const onPointer = (e: MouseEvent) => {
      if (!accountMenuRef.current?.contains(e.target as Node)) setAccountMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAccountMenuOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [accountMenuOpen]);

  // Auto-collapse on narrow viewports (only when user hasn't manually toggled)
  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${COLLAPSE_BREAKPOINT - 1}px)`);
    const handleChange = (e: MediaQueryListEvent) => {
      if (!userToggledRef.current) setCollapsed(e.matches);
    };
    mql.addEventListener('change', handleChange);
    return () => mql.removeEventListener('change', handleChange);
  }, []);

  const handleToggle = useCallback(() => {
    userToggledRef.current = true;
    setCollapsed((prev) => {
      const next = !prev;
      try { window.localStorage.setItem(STORAGE_KEY, String(next)); } catch { /* ignore */ }
      return next;
    });
  }, []);

  const handleLogout = (): void => {
    logout();
    navigate('/login', { replace: true });
  };

  const isAdmin = user?.role === 'leadership' || user?.role === 'admin';

  return (
    <div className="flex h-screen overflow-hidden bg-page-bg">
      {/* ── Sidebar ── */}
      <aside
        className={cn(
          'flex flex-col border-r border-border-sidebar bg-white transition-[width] duration-200 ease-in-out',
          collapsed ? 'w-[72px]' : 'w-[var(--spacing-sidebar)]',
        )}
      >
        {/* Logo + collapse toggle */}
        <div
          className={cn(
            'flex h-[var(--spacing-topbar)] shrink-0 items-center border-b border-outlines',
            collapsed ? 'justify-center px-2' : 'gap-2.5 px-4',
          )}
        >
          {!collapsed && (
            <>
              <div className="flex h-8 w-8 items-center justify-center rounded-[var(--radius-input)] bg-primary text-sm font-bold text-white">
                <ShieldCheck size={16} />
              </div>
              <span className="text-lg font-bold text-text-primary">DeliverPro</span>
            </>
          )}
          <button
            type="button"
            onClick={handleToggle}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            className={cn(
              'flex h-8 w-8 items-center justify-center rounded-[var(--radius-input)] text-text-secondary transition-colors hover:bg-subtle-bg',
              !collapsed && 'ml-auto',
            )}
          >
            {collapsed ? <PanelLeftOpen size={20} /> : <PanelLeftClose size={20} />}
          </button>
        </div>

        {/* Nav + account in single scroll container */}
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
          <nav className={cn('flex flex-col gap-1 py-4', collapsed ? 'px-2' : 'px-3')}>
            {/*
              No "Workspace" section label. It was the only group label in a single-group nav, so it
              labelled nothing the user could distinguish and just consumed vertical space.
              The collapsed-state divider below is KEPT: collapsed, the nav is a bare icon column
              directly under the header, and the rule is what separates the two. It is not a
              leftover of the label.
            */}
            {collapsed && <div className="mx-2 mb-2 border-t border-outlines" />}

            {NAV_ITEMS.filter(
              (item) =>
                (!item.adminOnly || isAdmin) && (!item.intakes || canViewIntakes(user?.role)),
            ).map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                title={collapsed ? item.label : undefined}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-2.5 rounded-[var(--radius-nav)] py-2.5 text-base font-medium transition-colors',
                    collapsed ? 'justify-center px-0' : 'px-3',
                    isActive
                      ? 'bg-primary-subtle text-primary'
                      : 'text-text-secondary hover:bg-subtle-bg',
                  )
                }
              >
                <span className="shrink-0">{item.icon}</span>
                {!collapsed && <span className="truncate">{item.label}</span>}
              </NavLink>
            ))}
          </nav>

          {/* Account popup — Slack-style */}
          <div ref={accountMenuRef} className="relative mt-auto border-t border-outlines">
            <button
              type="button"
              onClick={() => setAccountMenuOpen((o) => !o)}
              title={collapsed ? (user?.email ?? '') : undefined}
              aria-haspopup="menu"
              aria-expanded={accountMenuOpen}
              className={cn(
                'flex w-full items-center py-3 text-left transition-colors hover:bg-subtle-bg',
                collapsed ? 'justify-center px-2' : 'gap-2.5 px-4',
              )}
            >
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-subtle text-sm font-bold text-primary">
                {initials(user?.name)}
              </div>
              {!collapsed && (
                <>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-base font-medium text-text-primary">
                      {user?.name ?? user?.email ?? 'User'}
                    </div>
                    {user?.email && (
                      <div className="truncate text-xs text-text-secondary">{user.email}</div>
                    )}
                  </div>
                  <ChevronUp
                    size={16}
                    className={cn(
                      'shrink-0 text-text-secondary transition-transform',
                      !accountMenuOpen && 'rotate-180',
                    )}
                  />
                </>
              )}
            </button>

            {accountMenuOpen && (
              <div
                role="menu"
                className={cn(
                  'absolute bottom-full z-10 mb-2 flex flex-col rounded-[var(--radius-card)] border border-outlines bg-white py-1 shadow-lg',
                  collapsed ? 'left-full ml-2 w-48' : 'left-3 right-3',
                )}
              >
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => { setAccountMenuOpen(false); handleLogout(); }}
                  className="flex items-center gap-2.5 px-3 py-2 text-left text-sm font-medium text-text-secondary hover:bg-subtle-bg"
                >
                  <LogOut size={16} className="shrink-0" />
                  <span>Sign out</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* ── Main content ── */}
      <div className="flex flex-1 flex-col overflow-hidden">
        <header className="flex h-[var(--spacing-topbar)] shrink-0 items-center gap-3 border-b border-outlines bg-white px-6">
          <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-sm">
            <span className="text-text-secondary">DeliverPro</span>
            <span className="text-text-placeholder">/</span>
            <span className="font-semibold text-text-primary">
              {NAV_ITEMS.find((item) =>
                location.pathname === item.to || location.pathname.startsWith(`${item.to}/`),
              )?.label ?? 'Home'}
            </span>
          </nav>
        </header>
        <main className="flex-1 overflow-y-auto p-[var(--spacing-page)]">{children}</main>
      </div>
    </div>
  );
}
